import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Tab, Nav, NavLink } from 'react-bootstrap';

import Toolbar from '../../components/common/Toolbar';
import Panel from '../../components/common/Panel';
import { getRetailer, setRetailer, setRetailerTab } from '../../store/actions/retailer';
import BackTop from '../../components/common/BackTop';
import { CymbioSpinner } from '../../components/common/Spinner';
import PageLayout from '../../components/common/PageLayout';
import ProductsPermissionsContainer from '../blocks/ProductsPermissionsContainer';
import { RetailerPricingPage } from '../../components/blocks/RetailerPricingPage/RetailerPricingPage';
import RetailerInfo from '../../components/blocks/RetailerInfo';
import RetailerDetailsContainer from '../blocks/RetailerDetailsContainer';
import RetailerPermissionContainer from '../blocks/RetailerPermissionContainer';
import { STATUS, useRequiredRetailerIntegrationConfig } from '../../hooks/useRequiredRetailerIntegrationConfig';

export default function SingleRetailerPageContainer() {
  const dispatch = useDispatch();
  const queryParams = useParams();
  const retailerId = Number(queryParams.retailerId);
  const {
    name: retailerName,
    retailer: retailerInfo,
    ...retailerProperties
  } = useSelector((state) => state.retailer.current);
  const { isLoading, currentTab } = useSelector((state) => state.retailer);
  const reorderPermission = useSelector((state) => state.role?.supplier?.is_reorder_enabled);
  const { retailerIntegrationConfigStatus } = useRequiredRetailerIntegrationConfig();
  mergeRetailerPropertiesWithCurrent(retailerProperties, retailerInfo);

  const [tab, setTab] = useState(currentTab);

  useEffect(() => {
    dispatch(getRetailer(retailerId));
    return () => {
      dispatch(setRetailer({}));
    };
  }, [dispatch, retailerId]);

  const changeTab = (newTab) => {
    dispatch(setRetailerTab(newTab));
    setTab(newTab);
  };

  const items = [
    { key: 'settings', label: 'Settings' },
    { key: 'dropship', label: 'Catalogue Management' },
    { key: 'reorder', label: 'Reorder Catalogue Management', condition: () => reorderPermission },
    { key: 'pricing', label: 'Pricing' },
    { key: 'terms', label: 'Commercial Terms' },
  ];

  if (isLoading || !retailerInfo) {
    return <CymbioSpinner shouldCenterVerticallyInViewport />;
  }

  return (
    <>
      {retailerIntegrationConfigStatus === STATUS.MISSING ? null : (
        <Panel>
          <BackTop text={retailerName} />
        </Panel>
      )}

      <PageLayout>
        {retailerInfo && <RetailerInfo retailer={retailerInfo} />}

        <Toolbar grow color="transparent" margin_bottom>
          <Nav>
            {items
              .filter((item) => !item.condition || item.condition())
              .map((item) => (
                <Nav.Item key={item.key}>
                  <NavLink onClick={() => changeTab(item.key)} active={item.key === tab}>
                    {item.label}
                  </NavLink>
                </Nav.Item>
              ))}
          </Nav>
        </Toolbar>

        <Tab.Container id="retailerTab" activeKey={tab}>
          <Tab.Content>
            <Tab.Pane eventKey="settings">
              <RetailerDetailsContainer />
            </Tab.Pane>

            <Tab.Pane eventKey="dropship" unmountOnExit>
              <ProductsPermissionsContainer permissionType="dropship" id={retailerId} />
            </Tab.Pane>

            <Tab.Pane eventKey="reorder" unmountOnExit>
              <ProductsPermissionsContainer permissionType="reorder" id={retailerId} />
            </Tab.Pane>

            <Tab.Pane eventKey="terms" mountOnEnter={true}>
              <RetailerPermissionContainer id={retailerId} />
            </Tab.Pane>

            <Tab.Pane eventKey="pricing" mountOnEnter={true} unmountOnExit>
              <RetailerPricingPage id={retailerId} />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </PageLayout>
    </>
  );
}

function mergeRetailerPropertiesWithCurrent(retailer, current) {
  const retailerPropertiesToMergeWithCurrent = ['retailer_display_name'];
  retailerPropertiesToMergeWithCurrent.forEach((property) => {
    if (retailer.hasOwnProperty(property) && !current.hasOwnProperty(property)) {
      current[property] = retailer[property];
    }
  });
}
